/***********************/
/* Angular + external */
import { Component, forwardRef, ElementRef, ChangeDetectionStrategy, Input } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { NG_VALUE_ACCESSOR } from "@angular/forms";
/***********************/
/* BFX */
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { ICTenorValueDateModel } from "./models/bfx-interface-controls.models";
import { ICNInputControlState } from "./models/bfx-interface-controls.enums";
// models

export const VALUE_DATE_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => BfxNDFValueDateComponent),
  multi: true,
};

import { SysFeatures, SystemConfigPromiseService } from "shared/cubiq-services";
import { LoggerService } from "@bfx/logger";
import { BfxValueDateComponent } from "app/order-entry/interface/controls/bfx-value-date.component";
import { OENEntryType } from "app/order-entry/data/enums/oe-entry-type.enum";

const logger = LoggerService.for(__dirname);

@Component({
  selector: "bfx-ndf-value-date",
  templateUrl: "./html/bfx-value-date.html",
  providers: [VALUE_DATE_VALUE_ACCESSOR],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class BfxNDFValueDateComponent extends BfxValueDateComponent {
  private ndf1MOnly: boolean = false;

  /**
   * When true, also disable NDF values dates other than 1M - used for enforcing 1M of algos
   */
  @Input("ndf1MOnlyOverride") ndf1MOnlyOverride: boolean;

  /* CONSTRUCTOR */
  constructor(
    element: ElementRef,
    translationService: TranslateService,
    sysConfigService: SystemConfigPromiseService,
  ) {
    super(element, translationService, sysConfigService);
    this.entry_type = OENEntryType.NDF;
  }

  protected override initializeReadOnlyFlag() {
    this.sysConfigService.getSystemConfigBoolean(SysFeatures.ndf1MOnly).then((value) => {
      if (this.ndf1MOnlyOverride) {
        value = true;
      }
      this.fwdEnabled = !value;
      this.ndf1MOnly = value;
      if (!this.fwdEnabled) {
        // Disable when fwds are not supported - otherwise leave at value that was there
        this.data.disabled = true;
      }
      if (this.ndf1MOnly) {
        this.lockInputControl();
        this.dataset = this.input_dataset;
      }
    });
  }

  /**
   * Override this setter to filter out all non-1M tenors if the ndf1MOnly flag is set
   */
  public override set dataset(value: any) {
    this.input_dataset = value;
    if (this.input_dataset) {
      let prov: Array<ICTenorValueDateModel> = [];
      let tenorFound: boolean = false;
      let rePopulate: boolean = false;
      if (
        this.input_dataset.tenorToValueAndFixingDateSet instanceof Array &&
        this.input_dataset.tenorToValueAndFixingDateSet.length > 0
      ) {
        // GUARD
        this.input_dataset.tenorToValueAndFixingDateSet.forEach((element) => {
          if (this.ndf1MOnly && element.tenor !== CubiqTS.Tenor.M1) {
            return;
          }
          const valueDate = new Date(
            element.valueDate.year,
            element.valueDate.month - 1,
            element.valueDate.day,
          );
          const fixingDate = new Date(
            element.fixingDate.year,
            element.fixingDate.month - 1,
            element.fixingDate.day,
          );
          let tvd = new ICTenorValueDateModel(element.tenor, valueDate, fixingDate);
          prov.push(tvd);
          if (this.data.value == null || this.ndf1MOnly) {
            if (CubiqTS.Tenor.M1 == element.tenor) {
              tenorFound = true;
              rePopulate = true;
              this.data.value = tvd;
              this.data.valueDate = tvd.valueDate;
              this.onChange(this.data.value);
            }
          } else if (this.data.value && this.data.value.tenor == element.tenor) {
            tenorFound = true;
            this.data.value.valueDate = valueDate;
            this.data.valueDate = this.data.value.valueDate;
            this.data.value.fixingDate = fixingDate;
            this.onChange(this.data.value);
          }
        });
      }

      if (prov instanceof Array && prov.length > 0) {
        // GUARD
        this.data.allTenorValueDates = prov;
        if (this.input_dataset.holidays) {
          let holidays: Array<Date> = this.input_dataset.holidays.map((standardDate) => {
            let hol = new Date(
              standardDate.date.year,
              standardDate.date.month - 1,
              standardDate.date.day,
            );
            if (
              this.data.value &&
              this.data.value.tenor == undefined &&
              this.data.value.valueDate
            ) {
              if (hol.valueOf() == this.data.value.valueDate.valueOf()) {
                this.data.value = null;
                this.onChange(this.data.value);
                this.data.state = ICNInputControlState.INVALID;
                this.reEvaluateFixingDate.emit(null);
              }
            }
            return hol;
          });
          // add days from today to spot next as these shouldnt be allowed either
          const snDate = this.getSNDate(prov);
          if (snDate) {
            let now = new Date();
            for (let d = now; d < snDate; d.setDate(d.getDate() + 1)) {
              holidays.push(new Date(d));
            }
          }

          this.data.holidays = holidays;
        }
        if (this.data.value && this.data.value.tenor != undefined && !tenorFound) {
          if (!tenorFound) {
            this.data.value = null;
            this.onChange(this.data.value);
            this.data.state = ICNInputControlState.INVALID;
          }
          if (rePopulate) {
            this.onChange(this.data.value);
          }
        }
      }
    } else {
      this.data.holidays = null;
      this.data.allTenorValueDates = null;
      this.data.tenorValueDates = null;
      this.data.value = new ICTenorValueDateModel(CubiqTS.Tenor.M1, null);
      this.onChange(this.data.value);
      this.updateInternalLockAccordingToDatasetSize();
    }
    this.searchTenors();
    this.validateState();
  }

  protected override applyLocking(): void {
    if (this.ndf1MOnly) {
      this.locked = true;
    } else {
      super.applyLocking();
    }
  }
}
